/**
 * Defensive decoding of SSE payloads.
 *
 * The stream is authenticated and first-party, but a dashboard that renders NaN
 * or throws inside a React tree because one field arrived null is worse than one
 * that admits the payload was rejected. Every decoder returns `null` on a shape
 * violation and the store counts those rejections, so the footer can report them
 * instead of quietly rendering half a panel.
 */
import type {
  AgentCounts,
  AgentVersion,
  AlertCondition,
  AlertDetails,
  AlertEvidence,
  AlertPayload,
  AlertResponseAction,
  AlertResponseTarget,
  AlertThreat,
  DetectionAlert,
  DetectionPage,
  DetectionSeverity,
  MitreRef,
  AmsiScan,
  AlertSeverity,
  BatchingMetrics,
  DnsDetails,
  FleetPayload,
  IngestMetrics,
  IngestTotals,
  LatencyMs,
  LogBacklog,
  LogDetail,
  LogDetails,
  LogFacets,
  LogFrame,
  LogRow,
  MetricsPayload,
  ProcessNode,
  ProcessNodeSource,
  ProcessTimelineItem,
  ProcessTimelinePage,
  ProcessTimelineSummary,
  ProcessTree,
  RejectionBucket,
  RuntimeMetrics,
  SilenceState,
  SilentAgent,
  SnapshotPayload,
  TenantVolume,
  ThroughputPoint,
} from './types.ts'

type Rec = Record<string, unknown>

const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v)

function num(o: Rec, k: string): number | null {
  const v = o[k]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function str(o: Rec, k: string): string | null {
  const v = o[k]
  return typeof v === 'string' ? v : null
}

function bool(o: Rec, k: string): boolean {
  return o[k] === true
}

/**
 * A field the contract declares nullable.
 *
 * Distinct from `num`/`str` on purpose: those fold "absent" and "null" into the
 * same failure, which is right for a required field. Here null is *data* — no
 * contracted quota, an endpoint with no baseline — and the difference decides
 * whether the UI shows a percentage or refuses to invent one.
 */
function nullableNum(o: Rec, k: string): number | null {
  const v = o[k]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function nullableStr(o: Rec, k: string): string | null {
  const v = o[k]
  return typeof v === 'string' && v !== '' ? v : null
}

/** Booleano anulável: só `true`/`false` literais contam; o resto é "não sabemos". */
function nullableBool(o: Rec, k: string): boolean | null {
  const v = o[k]
  return typeof v === 'boolean' ? v : null
}

function list(o: Rec, k: string): unknown[] | null {
  const v = o[k]
  return Array.isArray(v) ? v : null
}

/**
 * Uma lista de strings do contrato, defensiva.
 *
 * Elementos que não são string (ou são vazios) caem fora, e "ausente", "não é
 * array" e "array vazio" colapsam para `null` — porque para quem só desenha o
 * que veio (§20) não há diferença entre um `related_ip` vazio e um ausente.
 */
function strList(o: Rec, k: string): string[] | null {
  const v = o[k]
  if (!Array.isArray(v)) return null
  const out = v.filter((item): item is string => typeof item === 'string' && item !== '')
  return out.length === 0 ? null : out
}

function child(o: Rec, k: string): Rec | null {
  const v = o[k]
  return isRec(v) ? v : null
}

/** Decode every element, dropping the ones that fail. */
function decodeAll<T>(items: unknown[], decode: (v: unknown) => T | null): T[] {
  const out: T[] = []
  for (const item of items) {
    const decoded = decode(item)
    if (decoded !== null) out.push(decoded)
  }
  return out
}

export function decodeIngest(v: unknown): IngestMetrics | null {
  if (!isRec(v)) return null
  const received = num(v, 'events_per_sec_received')
  const accepted = num(v, 'events_per_sec_accepted')
  const rejected = num(v, 'events_per_sec_rejected')
  const depth = num(v, 'channel_depth')
  const capacity = num(v, 'channel_capacity')
  const connections = num(v, 'active_connections')
  if (
    received === null ||
    accepted === null ||
    rejected === null ||
    depth === null ||
    capacity === null ||
    connections === null
  ) {
    return null
  }
  // Trust the server's percentage, but recompute when it is missing or absurd.
  const reported = num(v, 'channel_depth_pct')
  const derived = capacity > 0 ? (depth / capacity) * 100 : 0
  const pct = reported !== null && reported >= 0 && reported <= 1000 ? reported : derived
  return {
    events_per_sec_received: received,
    events_per_sec_accepted: accepted,
    events_per_sec_rejected: rejected,
    channel_depth: depth,
    channel_capacity: capacity,
    channel_depth_pct: pct,
    active_connections: connections,
  }
}

/**
 * Cumulative totals.
 *
 * Absolute figures, so there is nothing to accumulate here and nothing to
 * difference: the six counters are copied as they arrived. A missing counter
 * rejects the *block*, not the tick — `decodeMetrics` keeps going with
 * `totals: null`, because the alternative is losing latency, rejections and the
 * chart over one absent field.
 */
export function decodeTotals(v: unknown): IngestTotals | null {
  if (!isRec(v)) return null
  const received = num(v, 'received')
  const accepted = num(v, 'accepted')
  const rejected = num(v, 'rejected')
  const todayReceived = num(v, 'today_received')
  const todayAccepted = num(v, 'today_accepted')
  const todayRejected = num(v, 'today_rejected')
  if (
    received === null ||
    accepted === null ||
    rejected === null ||
    todayReceived === null ||
    todayAccepted === null ||
    todayRejected === null
  ) {
    return null
  }

  // -1 is the contract's "no confirmation yet", and it is also the right answer
  // for a payload that omits the field: age unknown, not zero seconds ago.
  const age = num(v, 'age_seconds') ?? -1

  return {
    received,
    accepted,
    rejected,
    today_received: todayReceived,
    today_accepted: todayAccepted,
    today_rejected: todayRejected,
    // The contract pairs `-1` with `stale: true`. When only the age says so, the
    // partial figure still gets marked: a badge the operator can dismiss costs
    // nothing, while an unmarked number that is millions short costs a wrong
    // decision.
    stale: bool(v, 'stale') || age < 0,
    age_seconds: age,
  }
}

export function decodeRejection(v: unknown): RejectionBucket | null {
  if (!isRec(v)) return null
  const reason = str(v, 'reason')
  const count = num(v, 'count')
  if (reason === null || count === null) return null
  return { reason, count }
}

export function decodeLatency(v: unknown): LatencyMs | null {
  if (!isRec(v)) return null
  const p50 = num(v, 'p50')
  const p95 = num(v, 'p95')
  const p99 = num(v, 'p99')
  if (p50 === null || p95 === null || p99 === null) return null
  return { p50, p95, p99 }
}

export function decodeBatching(v: unknown): BatchingMetrics | null {
  if (!isRec(v)) return null
  const size = num(v, 'avg_batch_size')
  const flush = num(v, 'avg_flush_ms')
  if (size === null || flush === null) return null
  return { avg_batch_size: size, avg_flush_ms: flush }
}

export function decodeRuntime(v: unknown): RuntimeMetrics | null {
  if (!isRec(v)) return null
  const mem = num(v, 'managed_memory_mb')
  const pause = num(v, 'gc_pause_ms_last_minute')
  const gen2 = num(v, 'gen2_collections_last_minute')
  if (mem === null || pause === null || gen2 === null) return null
  return {
    managed_memory_mb: mem,
    gc_pause_ms_last_minute: pause,
    gen2_collections_last_minute: gen2,
  }
}

export function decodeThroughputPoint(v: unknown): ThroughputPoint | null {
  if (!isRec(v)) return null
  const t = str(v, 't')
  const eps = num(v, 'events_per_sec')
  if (t === null || eps === null) return null
  // A point that cannot be placed on a time axis is useless.
  if (!Number.isFinite(Date.parse(t))) return null
  return { t, events_per_sec: eps }
}

export function decodeMetrics(v: unknown): MetricsPayload | null {
  if (!isRec(v)) return null
  const serverTime = str(v, 'server_time')
  const ingest = decodeIngest(v['ingest'])
  const latency = decodeLatency(v['latency_ms'])
  const batching = decodeBatching(v['batching'])
  const runtime = decodeRuntime(v['runtime'])
  if (
    serverTime === null ||
    ingest === null ||
    latency === null ||
    batching === null ||
    runtime === null
  ) {
    return null
  }
  const rejections = list(v, 'rejections_last_hour')
  const series = list(v, 'throughput_series')
  return {
    server_time: serverTime,
    ingest,
    // Deliberately outside the null-check above: an absent or malformed totals
    // block must not take the rest of the tick down with it.
    totals: decodeTotals(v['totals']),
    latency_ms: latency,
    batching,
    runtime,
    rejections_last_hour: rejections ? decodeAll(rejections, decodeRejection) : [],
    throughput_series: series ? decodeAll(series, decodeThroughputPoint) : [],
  }
}

export function decodeAgentCounts(v: unknown): AgentCounts | null {
  if (!isRec(v)) return null
  const total = num(v, 'total_provisioned')
  const connected = num(v, 'connected')
  const silence = num(v, 'anomalous_silence')
  const expected = num(v, 'expected_offline')
  const never = num(v, 'never_connected')
  if (
    total === null ||
    connected === null ||
    silence === null ||
    expected === null ||
    never === null
  ) {
    return null
  }
  return {
    total_provisioned: total,
    connected,
    anomalous_silence: silence,
    expected_offline: expected,
    never_connected: never,
  }
}

const SILENCE_STATES: readonly SilenceState[] = [
  'never_connected',
  'anomalous_silence',
  'expected_offline',
]

/**
 * An unknown `state` falls back to `expected_offline`, never to
 * `anomalous_silence`.
 *
 * The fallback has to be the quiet one: inventing a security finding out of a
 * field the decoder did not recognise would put a red row on the screen that no
 * one can act on, and the panel's whole credibility rests on that row meaning
 * something.
 */
function silenceStateOf(v: unknown): SilenceState {
  return SILENCE_STATES.find((state) => state === v) ?? 'expected_offline'
}

export function decodeSilentAgent(v: unknown): SilentAgent | null {
  if (!isRec(v)) return null
  const id = str(v, 'endpoint_id')
  const hostname = str(v, 'hostname')
  const silentFor = num(v, 'silent_for_minutes')
  if (id === null || hostname === null || silentFor === null) return null
  return {
    endpoint_id: id,
    hostname,
    tenant_name: str(v, 'tenant_name') ?? '—',
    // Null is contractual for `never_connected`, and stays null: rendering a
    // fabricated "last seen" for a host that never reported would be a lie.
    last_seen: nullableStr(v, 'last_seen'),
    silent_for_minutes: silentFor,
    state: silenceStateOf(v['state']),
    typical_active_hours: nullableStr(v, 'typical_active_hours'),
  }
}

export function decodeTenant(v: unknown): TenantVolume | null {
  if (!isRec(v)) return null
  const id = str(v, 'tenant_id')
  const name = str(v, 'name')
  const eps = num(v, 'events_per_sec')
  const endpoints = num(v, 'endpoints')
  if (id === null || name === null || eps === null || endpoints === null) return null

  // No contracted quota is a legitimate state, not missing data. The percentage
  // is only derived when there IS a ceiling to divide by; otherwise it stays
  // null and the panel shows absolute volume with no bar.
  const quota = nullableNum(v, 'quota_events_per_sec')
  const usage = nullableNum(v, 'quota_usage_pct')
  const ratio = num(v, 'events_per_endpoint')

  return {
    tenant_id: id,
    name,
    events_per_sec: eps,
    endpoints,
    events_per_endpoint: ratio ?? (endpoints > 0 ? eps / endpoints : 0),
    quota_events_per_sec: quota,
    quota_usage_pct: quota === null ? null : (usage ?? (quota > 0 ? (eps / quota) * 100 : 0)),
    is_outlier: bool(v, 'is_outlier'),
  }
}

export function decodeAgentVersion(v: unknown): AgentVersion | null {
  if (!isRec(v)) return null
  const version = str(v, 'version')
  const count = num(v, 'count')
  if (version === null || count === null) return null
  return { version, count, has_known_issue: bool(v, 'has_known_issue') }
}

export function decodeFleet(v: unknown): FleetPayload | null {
  if (!isRec(v)) return null
  const agents = decodeAgentCounts(v['agents'])
  if (agents === null) return null
  const silent = list(v, 'silent_agents')
  const tenants = list(v, 'tenants')
  const versions = list(v, 'agent_versions')
  return {
    agents,
    silent_agents: silent ? decodeAll(silent, decodeSilentAgent) : [],
    tenants: tenants ? decodeAll(tenants, decodeTenant) : [],
    agent_versions: versions ? decodeAll(versions, decodeAgentVersion) : [],
    enrollments_last_24h: num(v, 'enrollments_last_24h') ?? 0,
    reconnect_rate_per_min: num(v, 'reconnect_rate_per_min') ?? 0,
    reference_stale: bool(v, 'reference_stale'),
    // -1 is the contract's "no successful cycle since boot", and it is also the
    // right answer for a payload that omits the field: age unknown, not zero.
    reference_age_seconds: num(v, 'reference_age_seconds') ?? -1,
  }
}

export function decodeSnapshot(v: unknown, receivedAt: string): SnapshotPayload | null {
  if (!isRec(v)) return null
  const metrics = decodeMetrics(child(v, 'metrics'))
  const fleet = decodeFleet(child(v, 'fleet'))
  if (metrics === null || fleet === null) return null

  // Retained alerts, so a reconnect does not open onto an empty rail. They are
  // sent oldest first; the store reverses and dedupes by id.
  const alerts = list(v, 'alerts')

  return {
    metrics,
    fleet,
    alerts: alerts === null ? [] : decodeAll(alerts, (item) => decodeAlert(item, receivedAt)),
  }
}

const SEVERITIES: readonly AlertSeverity[] = ['info', 'warning', 'critical']

function severityOf(v: unknown): AlertSeverity {
  return SEVERITIES.find((s) => s === v) ?? 'info'
}

export function decodeAlert(v: unknown, receivedAt: string): AlertPayload | null {
  if (!isRec(v)) return null
  const id = str(v, 'id')
  const message = str(v, 'message')
  if (id === null || message === null) return null
  return {
    id,
    severity: severityOf(v['severity']),
    kind: str(v, 'kind') ?? 'unknown',
    message,
    tenant_name: str(v, 'tenant_name'),
    occurred_at: str(v, 'occurred_at') ?? receivedAt,
  }
}

/* ── logs ─────────────────────────────────────────────────────────────────── */

/**
 * A linha de log.
 *
 * Cinco campos são obrigatórios e recusam a linha quando faltam; os dois
 * identificadores (`id`, `event_id`) e o resto são nulos com significado —
 * `tenant_name`/`hostname` nulos renderizam degradado, com o `endpoint_id`
 * intacto, e nada aqui os fabrica.
 */
export function decodeLogRow(v: unknown): LogRow | null {
  if (!isRec(v)) return null
  const eventTime = str(v, 'event_time')
  const receivedAt = str(v, 'received_at')
  const tenantId = str(v, 'tenant_id')
  const endpointId = str(v, 'endpoint_id')
  const eventType = str(v, 'event_type')
  if (
    eventTime === null ||
    receivedAt === null ||
    tenantId === null ||
    endpointId === null ||
    eventType === null
  ) {
    return null
  }
  return {
    id: nullableNum(v, 'id'),
    event_id: nullableStr(v, 'event_id'),
    event_time: eventTime,
    received_at: receivedAt,
    tenant_id: tenantId,
    tenant_name: nullableStr(v, 'tenant_name'),
    endpoint_id: endpointId,
    hostname: nullableStr(v, 'hostname'),
    event_type: eventType,
    source: nullableStr(v, 'source'),
    native_id: nullableStr(v, 'native_id'),
    record_id: nullableNum(v, 'record_id'),
    payload_size: nullableNum(v, 'payload_size'),
  }
}

export function decodeLogFrame(v: unknown): LogFrame | null {
  if (!isRec(v)) return null

  const serverTime = str(v, 'server_time')
  const rows = list(v, 'rows')
  const matched = num(v, 'matched')
  const delivered = num(v, 'delivered')
  const windowMs = num(v, 'window_ms')

  if (serverTime === null || matched === null || delivered === null || windowMs === null) {
    return null
  }

  return {
    server_time: serverTime,
    rows: rows ? decodeAll(rows, decodeLogRow) : [],
    matched,
    delivered,
    // Derivado quando ausente, NUNCA zerado: um sampled_out fabricado como 0
    // afirma completude que não houve, e a tela desenharia a mentira sem
    // reclamar. matched - delivered é sempre a resposta certa.
    sampled_out: num(v, 'sampled_out') ?? matched - delivered,
    // Zero é o caso normal e é o único default seguro aqui: inventar um lapped
    // positivo dispararia alarme falso.
    lapped: num(v, 'lapped') ?? 0,
    // 'partial' como fallback, e não 'full'. O default tem que ser o CAUTELOSO:
    // um coverage desconhecido tratado como 'full' esconderia cobertura parcial,
    // que é justamente o modo de falha que este campo existe para denunciar.
    coverage: str(v, 'coverage') ?? 'partial',
    instances: num(v, 'instances') ?? 1,
    window_ms: windowMs,
  }
}

/**
 * O `backlog`. `degraded: true` é "não consegui ler o histórico", nunca "não há
 * eventos" — por isso não recusa a linha, é dado que a tela mostra (§4.7).
 */
export function decodeLogBacklog(v: unknown): LogBacklog | null {
  if (!isRec(v)) return null
  const serverTime = str(v, 'server_time')
  if (serverTime === null) return null
  const rows = list(v, 'rows')
  return {
    server_time: serverTime,
    rows: rows ? decodeAll(rows, decodeLogRow) : [],
    degraded: bool(v, 'degraded'),
  }
}

/**
 * As facetas — o resumo estruturado do evento (§HERMES_MIGRATION.md).
 *
 * Ao contrário dos outros decodificadores, este NUNCA devolve `null`: o contrato
 * garante que `facets` sempre existe, e a ficha de detalhe conta com isso sem
 * checar. O que é defensivo é cada CAMPO — ausência ou forma inválida vira
 * `null`, e a interface omite o que for null (§20). Os enums ficam como texto:
 * um valor novo que o servidor introduza renderiza como string em vez de sumir,
 * o mesmo tratamento que `RejectionBucket.reason` e `AlertPayload.kind` recebem.
 *
 * Um `facets` ausente ou malformado — um servidor anterior ao campo, um payload
 * truncado — vira o objeto todo-nulo, que a ficha lê como "nada estruturado a
 * mostrar", nunca como erro.
 */
export function decodeLogFacets(v: unknown): LogFacets {
  const o: Rec = isRec(v) ? v : {}
  return {
    event_action: nullableStr(o, 'event_action'),
    event_category: strList(o, 'event_category'),
    event_outcome: nullableStr(o, 'event_outcome'),
    event_module: nullableStr(o, 'event_module'),
    event_provider: nullableStr(o, 'event_provider'),
    process_entity_id: nullableStr(o, 'process_entity_id'),
    process_parent_entity_id: nullableStr(o, 'process_parent_entity_id'),
    process_pid: nullableNum(o, 'process_pid'),
    process_parent_pid: nullableNum(o, 'process_parent_pid'),
    process_executable: nullableStr(o, 'process_executable'),
    process_name: nullableStr(o, 'process_name'),
    process_command_line: nullableStr(o, 'process_command_line'),
    process_integrity: nullableStr(o, 'process_integrity'),
    user_name: nullableStr(o, 'user_name'),
    user_domain: nullableStr(o, 'user_domain'),
    user_sid: nullableStr(o, 'user_sid'),
    host_hostname: nullableStr(o, 'host_hostname'),
    host_os: nullableStr(o, 'host_os'),
    source_ip: nullableStr(o, 'source_ip'),
    source_port: nullableNum(o, 'source_port'),
    destination_ip: nullableStr(o, 'destination_ip'),
    destination_port: nullableNum(o, 'destination_port'),
    network_transport: nullableStr(o, 'network_transport'),
    network_direction: nullableStr(o, 'network_direction'),
    dns_question: nullableStr(o, 'dns_question'),
    file_path: nullableStr(o, 'file_path'),
    file_name: nullableStr(o, 'file_name'),
    file_extension: nullableStr(o, 'file_extension'),
    registry_path: nullableStr(o, 'registry_path'),
    service_name: nullableStr(o, 'service_name'),
    service_state: nullableStr(o, 'service_state'),
    related_ip: strList(o, 'related_ip'),
    related_user: strList(o, 'related_user'),
    related_hash: strList(o, 'related_hash'),
    process_creator_entity_id: nullableStr(o, 'process_creator_entity_id'),
    process_creator_pid: nullableNum(o, 'process_creator_pid'),
  }
}

/**
 * A inspeção AMSI. Null quando o bloco não veio ou não é objeto — evento que não
 * é `amsi_scan`. Dentro dele, cada campo é anulável.
 *
 * `detected` fica null quando ausente ou de outro tipo: fabricar `false` diria
 * "o AMSI achou limpo", que é um veredito que o servidor não deu.
 * `content_truncated` é o contrário — o default seguro é não afirmar corte.
 */
export function decodeAmsi(v: unknown): AmsiScan | null {
  if (!isRec(v)) return null
  return {
    app_name: nullableStr(v, 'app_name'),
    content_name: nullableStr(v, 'content_name'),
    // O script vai como veio, inclusive espaço em branco: é evidência.
    content: str(v, 'content'),
    content_truncated: bool(v, 'content_truncated'),
    content_size: nullableNum(v, 'content_size'),
    original_size: nullableNum(v, 'original_size'),
    hash: nullableStr(v, 'hash'),
    scan_result: nullableNum(v, 'scan_result'),
    detected: nullableBool(v, 'detected'),
  }
}

export function decodeDns(v: unknown): DnsDetails | null {
  if (!isRec(v)) return null
  return {
    question_name: nullableStr(v, 'question_name'),
    question_type: nullableStr(v, 'question_type'),
    response_code: nullableNum(v, 'response_code'),
    answers: strList(v, 'answers'),
  }
}

/**
 * argv. Diferente de `strList`: um argumento VAZIO é argumento (`""` muda o
 * sentido de muita linha de comando), então só não-strings caem fora.
 */
function argList(o: Rec, k: string): string[] | null {
  const v = o[k]
  if (!Array.isArray(v)) return null
  const out = v.filter((item): item is string => typeof item === 'string')
  return out.length === 0 ? null : out
}

/**
 * `details` — campos lidos do payload (§12.1). Como `facets`, NUNCA devolve
 * null: ausente ou malformado vira o objeto todo-nulo, e a ficha omite o que
 * for null (§20).
 */
export function decodeLogDetails(v: unknown): LogDetails {
  const o: Rec = isRec(v) ? v : {}
  return {
    process_args: argList(o, 'process_args'),
    process_exit_code: nullableNum(o, 'process_exit_code'),
    registry_data: str(o, 'registry_data'),
    registry_data_type: nullableNum(o, 'registry_data_type'),
    registry_data_size: nullableNum(o, 'registry_data_size'),
    amsi: decodeAmsi(o['amsi']),
    dns: decodeDns(o['dns']),
    alert: decodeAlertDetails(o['alert']),
  }
}

/**
 * O detalhe. `payload` é estrutura ARBITRÁRIA — vem do agente e o servidor não a
 * interpreta —, então é copiada como veio, sem tentativa de tipar. `facets`, ao
 * contrário, é o resumo tipado das colunas, e sempre vem como objeto.
 */
export function decodeLogDetail(v: unknown): LogDetail | null {
  if (!isRec(v)) return null
  const row = decodeLogRow(child(v, 'row'))
  if (row === null) return null
  return {
    row,
    batch_id: nullableStr(v, 'batch_id'),
    agent_version: nullableStr(v, 'agent_version'),
    payload: v['payload'],
    facets: decodeLogFacets(v['facets']),
    details: decodeLogDetails(v['details']),
  }
}

/* ── processos (§12) ──────────────────────────────────────────────────────── */

const NODE_SOURCES: readonly ProcessNodeSource[] = ['process_create', 'first_event', 'unseen']

/**
 * Uma `source` desconhecida cai em `unseen`, o estado mais PARCIAL: tratar um
 * nó de origem incerta como `process_create` afirmaria que o nascimento foi
 * visto, e a tela tiraria a marca de dado parcial.
 */
function nodeSourceOf(v: unknown): ProcessNodeSource {
  return NODE_SOURCES.find((s) => s === v) ?? 'unseen'
}

/** O nó. Só `entity_id` é obrigatório — um `unseen` legítimo é só isso. */
export function decodeProcessNode(v: unknown): ProcessNode | null {
  if (!isRec(v)) return null
  const entityId = nullableStr(v, 'entity_id')
  if (entityId === null) return null
  return {
    entity_id: entityId,
    source: nodeSourceOf(v['source']),
    event_id: nullableNum(v, 'event_id'),
    tenant_id: nullableStr(v, 'tenant_id'),
    tenant_name: nullableStr(v, 'tenant_name'),
    endpoint_id: nullableStr(v, 'endpoint_id'),
    hostname: nullableStr(v, 'hostname'),
    started_at: nullableStr(v, 'started_at'),
    ended_at: nullableStr(v, 'ended_at'),
    exit_code: nullableNum(v, 'exit_code'),
    parent_entity_id: nullableStr(v, 'parent_entity_id'),
    creator_entity_id: nullableStr(v, 'creator_entity_id'),
    creator_pid: nullableNum(v, 'creator_pid'),
    parent_spoofed: bool(v, 'parent_spoofed'),
    pid: nullableNum(v, 'pid'),
    parent_pid: nullableNum(v, 'parent_pid'),
    name: nullableStr(v, 'name'),
    executable: nullableStr(v, 'executable'),
    command_line: nullableStr(v, 'command_line'),
    user_name: nullableStr(v, 'user_name'),
    user_domain: nullableStr(v, 'user_domain'),
    integrity: nullableStr(v, 'integrity'),
    host_os: nullableStr(v, 'host_os'),
    has_children: nullableBool(v, 'has_children'),
  }
}

/**
 * A árvore, uma camada. Sem `node` válido não há árvore; listas ausentes viram
 * vazias e elementos inválidos caem fora, sem derrubar o resto.
 */
export function decodeProcessTree(v: unknown): ProcessTree | null {
  if (!isRec(v)) return null
  const node = decodeProcessNode(v['node'])
  if (node === null) return null
  const ancestors = list(v, 'ancestors')
  const children = list(v, 'children')
  return {
    node,
    ancestors: ancestors ? decodeAll(ancestors, decodeProcessNode) : [],
    ancestors_truncated: bool(v, 'ancestors_truncated'),
    children: children ? decodeAll(children, decodeProcessNode) : [],
    children_truncated: bool(v, 'children_truncated'),
    creator: decodeProcessNode(v['creator']),
  }
}

export function decodeTimelineSummary(v: unknown): ProcessTimelineSummary {
  const o: Rec = isRec(v) ? v : {}
  return {
    event_action: nullableStr(o, 'event_action'),
    event_outcome: nullableStr(o, 'event_outcome'),
    process_entity_id: nullableStr(o, 'process_entity_id'),
    process_name: nullableStr(o, 'process_name'),
    process_executable: nullableStr(o, 'process_executable'),
    process_pid: nullableNum(o, 'process_pid'),
    process_command_line: nullableStr(o, 'process_command_line'),
    file_path: nullableStr(o, 'file_path'),
    dns_question: nullableStr(o, 'dns_question'),
    destination_ip: nullableStr(o, 'destination_ip'),
    destination_port: nullableNum(o, 'destination_port'),
    registry_path: nullableStr(o, 'registry_path'),
    related_hash: strList(o, 'related_hash'),
  }
}

/**
 * Um item da linha do tempo. `relation` desconhecida cai em `self`: tratá-la
 * como `child_spawn` apontaria o link "lançou…" para o processo errado.
 */
export function decodeTimelineItem(v: unknown): ProcessTimelineItem | null {
  if (!isRec(v)) return null
  const row = decodeLogRow(v['row'])
  if (row === null) return null
  return {
    row,
    relation: v['relation'] === 'child_spawn' ? 'child_spawn' : 'self',
    summary: decodeTimelineSummary(v['summary']),
  }
}

export function decodeTimelinePage(v: unknown): ProcessTimelinePage | null {
  if (!isRec(v)) return null
  const items = list(v, 'items')
  if (items === null) return null
  const decoded = decodeAll(items, decodeTimelineItem)
  const cursor = nullableStr(v, 'next_cursor')
  return {
    items: decoded,
    page_size: num(v, 'page_size') ?? decoded.length,
    // Sem cursor não há como pedir a próxima página: `has_more` sem ele seria um
    // botão que não faz nada.
    has_more: bool(v, 'has_more') && cursor !== null,
    next_cursor: cursor,
    order: str(v, 'order') ?? 'event_time_asc',
  }
}

/* ── detecções ────────────────────────────────────────────────────────────── */

const DETECTION_SEVERITIES: readonly DetectionSeverity[] = ['low', 'medium', 'high', 'critical']

/** Fora do vocabulário vira null — nem rebaixar nem promover um alerta (types.ts). */
export function severityOfDetection(v: unknown): DetectionSeverity | null {
  return DETECTION_SEVERITIES.find((s) => s === v) ?? null
}

export function decodeMitre(v: unknown): MitreRef | null {
  if (!isRec(v)) return null
  const id = nullableStr(v, 'id')
  return id === null ? null : { id, name: nullableStr(v, 'name') }
}

function mitreList(o: Rec, k: string): MitreRef[] {
  const items = list(o, k)
  return items === null ? [] : decodeAll(items, decodeMitre)
}

/** Risco 0–100; qualquer outra coisa é "não sabemos", não zero. */
function riskOf(o: Rec, k: string): number | null {
  const v = nullableNum(o, k)
  return v !== null && v >= 0 && v <= 100 ? v : null
}

function decodeResponseTarget(v: unknown): AlertResponseTarget | null {
  if (!isRec(v)) return null
  return {
    pid: nullableNum(v, 'pid'),
    name: nullableStr(v, 'name'),
    entity_id: nullableStr(v, 'entity_id'),
    executable: nullableStr(v, 'executable'),
    ip: nullableStr(v, 'ip'),
  }
}

/** Vocabulário aberto: `action`/`status` vão como vieram; só tipo errado vira null. */
export function decodeResponseAction(v: unknown): AlertResponseAction | null {
  if (!isRec(v)) return null
  return {
    action: nullableStr(v, 'action'),
    status: nullableStr(v, 'status'),
    timestamp: nullableStr(v, 'timestamp'),
    target: decodeResponseTarget(v['target']),
    detail: nullableStr(v, 'detail'),
  }
}

/** Ausente ou fora de forma vira lista vazia — o contrato diz "nunca null". */
function responseList(o: Rec): AlertResponseAction[] {
  const items = list(o, 'response')
  return items === null ? [] : decodeAll(items, decodeResponseAction)
}

export function decodeDetection(v: unknown): DetectionAlert | null {
  if (!isRec(v)) return null
  const row = decodeLogRow(v['row'])
  if (row === null) return null
  return {
    row,
    rule_id: nullableStr(v, 'rule_id'),
    rule_name: nullableStr(v, 'rule_name'),
    severity: severityOfDetection(v['severity']),
    risk_score: riskOf(v, 'risk_score'),
    mechanism: nullableStr(v, 'mechanism'),
    tactics: mitreList(v, 'tactics'),
    techniques: mitreList(v, 'techniques'),
    process_entity_id: nullableStr(v, 'process_entity_id'),
    process_name: nullableStr(v, 'process_name'),
    process_pid: nullableNum(v, 'process_pid'),
    process_command_line: nullableStr(v, 'process_command_line'),
    user_name: nullableStr(v, 'user_name'),
    host_hostname: nullableStr(v, 'host_hostname'),
    response: responseList(v),
  }
}

export function decodeDetectionPage(v: unknown): DetectionPage | null {
  if (!isRec(v)) return null
  const items = list(v, 'items')
  if (items === null) return null
  const decoded = decodeAll(items, decodeDetection)
  const cursor = nullableStr(v, 'next_cursor')
  return {
    items: decoded,
    page_size: num(v, 'page_size') ?? decoded.length,
    // Sem cursor não há próxima página, como na linha do tempo.
    has_more: bool(v, 'has_more') && cursor !== null,
    next_cursor: cursor,
    order: str(v, 'order') ?? 'id_desc',
  }
}

function decodeThreat(v: unknown): AlertThreat | null {
  if (!isRec(v)) return null
  return {
    framework: nullableStr(v, 'framework'),
    tactic: decodeMitre(v['tactic']),
    technique: decodeMitre(v['technique']),
  }
}

function decodeCondition(v: unknown): AlertCondition | null {
  if (!isRec(v)) return null
  return {
    stage: nullableNum(v, 'stage'),
    field: nullableStr(v, 'field'),
    operator: nullableStr(v, 'operator'),
    // Esperado e observado vão como vieram: são a prova do casamento.
    expected: str(v, 'expected'),
    observed: str(v, 'observed'),
    negated: bool(v, 'negated'),
  }
}

function decodeEvidence(v: unknown): AlertEvidence | null {
  if (!isRec(v)) return null
  return {
    stage: nullableNum(v, 'stage'),
    timestamp: nullableStr(v, 'timestamp'),
    action: nullableStr(v, 'action'),
    code: nullableStr(v, 'code'),
    entity_id: nullableStr(v, 'entity_id'),
    pid: nullableNum(v, 'pid'),
    process_name: nullableStr(v, 'process_name'),
    command_line: str(v, 'command_line'),
    parent_name: nullableStr(v, 'parent_name'),
    user: nullableStr(v, 'user'),
    file_path: nullableStr(v, 'file_path'),
    registry_path: nullableStr(v, 'registry_path'),
    destination: nullableStr(v, 'destination'),
    dns_question: nullableStr(v, 'dns_question'),
  }
}

/**
 * Uma `explanation` crua (a do challenge do Web Defense vem como o agente
 * mandou). Não-lista vira vazia; elementos inválidos caem fora.
 */
export function decodeExplanation(v: unknown): AlertCondition[] {
  return Array.isArray(v) ? decodeAll(v, decodeCondition) : []
}

/** `details.alert`. Listas ausentes viram vazias; elementos inválidos caem fora. */
export function decodeAlertDetails(v: unknown): AlertDetails | null {
  if (!isRec(v)) return null
  const pick = <T,>(k: string, decode: (x: unknown) => T | null): T[] => {
    const items = list(v, k)
    return items === null ? [] : decodeAll(items, decode)
  }
  return {
    rule_id: nullableStr(v, 'rule_id'),
    rule_name: nullableStr(v, 'rule_name'),
    rule_description: nullableStr(v, 'rule_description'),
    rule_version: nullableNum(v, 'rule_version'),
    ruleset: nullableStr(v, 'ruleset'),
    references: strList(v, 'references') ?? [],
    severity: severityOfDetection(v['severity']),
    risk_score: riskOf(v, 'risk_score'),
    mechanism: nullableStr(v, 'mechanism'),
    key: nullableStr(v, 'key'),
    threats: pick('threats', decodeThreat),
    explanation: pick('explanation', decodeCondition),
    evidence: pick('evidence', decodeEvidence),
    response: responseList(v),
  }
}

/**
 * Chave de deduplicação entre `backlog` e `logs`, que se sobrepõem de propósito
 * (§4.6): `(endpoint_id, received_at, record_id)`, com `native_id` como
 * desempate quando `record_id` é null, e os identificadores como último recurso.
 */
export function logKey(row: LogRow): string {
  const tail = row.record_id ?? row.native_id ?? row.event_id ?? row.id ?? ''
  return `${row.endpoint_id}|${row.received_at}|${tail}`
}

/** JSON.parse that never throws into an event handler. */
export function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}
